import { useMemo, useState } from "react";
import { FiBriefcase, FiClipboard, FiFile, FiGlobe, FiX } from "react-icons/fi";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { cn } from "utils/helpers";
import { scopeTypeStyle } from "newui/features/RolesPermissions/utils/scopeTypeStyles";
import type {
	PermissionGridEntry,
	ScopeOption,
	ScopeType,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

interface Step2ScopeSearchProps {
	scopeOptions: ScopeOption[];
	// F3: full selected objects (not keys) — a pick survives changing the search query.
	selectedScopes: ScopeOption[];
	onToggleScope: (option: ScopeOption) => void;
	onRemoveScope: (option: ScopeOption) => void;
	roleName: string | null;
	permissionGrid: PermissionGridEntry[];
	targetUserCount: number;
	isLoading?: boolean;
	onQueryChange?: (query: string) => void;
	// Template mode is company-wide only — scope picking is disabled with explanatory copy.
	scopeSelectionDisabled?: boolean;
}

// Identity key for a catalogue option. scope_id is null for the synthetic `global` option.
export const scopeKey = (type: ScopeType, id: number | null): string => `${type}:${id ?? "null"}`;

const TYPE_ORDER: ScopeType[] = ["global", "project_type", "project", "rfx"];
const TYPE_LABEL: Record<ScopeType, string> = {
	global: "Company-wide",
	project_type: "Project types",
	project: "Projects",
	rfx: "RFx events",
};
const TYPE_ICON: Record<ScopeType, JSX.Element> = {
	global: <FiGlobe size={16} />,
	project_type: <FiBriefcase size={16} />,
	project: <FiClipboard size={16} />,
	rfx: <FiFile size={16} />,
};

const emptyGroups = (): Record<ScopeType, ScopeOption[]> => ({
	global: [],
	project_type: [],
	project: [],
	rfx: [],
});

// Step 2: searchable type-to-filter scope picker — chips grouped by type (NOT a tree). The selected
// scope applies to ALL Step-1 users. The target role's permission grid renders inline, read-only.
export const Step2ScopeSearch = ({
	scopeOptions,
	selectedScopes,
	onToggleScope,
	onRemoveScope,
	roleName,
	permissionGrid,
	targetUserCount,
	isLoading = false,
	onQueryChange,
	scopeSelectionDisabled = false,
}: Step2ScopeSearchProps) => {
	const [query, setQuery] = useState("");

	const selectedKeys = useMemo(
		() => selectedScopes.map((option) => scopeKey(option.scope_type, option.scope_id)),
		[selectedScopes]
	);

	// Local narrowing on top of the server-side search — keeps typing responsive between fetches.
	const matches = useMemo(() => {
		const normalised = query.trim().toLowerCase();
		if (!normalised) {
			return scopeOptions;
		}
		return scopeOptions.filter(
			(option) =>
				option.label.toLowerCase().includes(normalised) ||
				(option.parent?.label ?? "").toLowerCase().includes(normalised)
		);
	}, [query, scopeOptions]);

	const groupedMatches = useMemo(() => {
		const groups = emptyGroups();
		matches.forEach((option) => groups[option.scope_type].push(option));
		return groups;
	}, [matches]);

	// F3: chips (and therefore the submit payload upstream) come from the stored selection objects,
	// never from the current (query-driven, capped) results page.
	const selectedByType = useMemo(() => {
		const groups = emptyGroups();
		selectedScopes.forEach((option) => groups[option.scope_type].push(option));
		return groups;
	}, [selectedScopes]);

	const hasSelection = selectedScopes.length > 0;

	const handleQuery = (value: string) => {
		setQuery(value);
		onQueryChange?.(value);
	};

	// Template mode is company-wide only — no scope picking (server hardcodes Global).
	if (scopeSelectionDisabled) {
		return (
			<div data-testid="wizard-step2">
				<div
					className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
					data-testid="scope-disabled-notice"
				>
					Templates assign access <span className="font-semibold">company-wide</span>. To scope
					access to specific projects or RFx events, assign a role instead (Back → Single role).
				</div>
				<ReadOnlyPermissionGrid roleName={roleName} entries={permissionGrid} />
			</div>
		);
	}

	return (
		<div data-testid="wizard-step2">
			<p className="mb-3 text-sm text-gray-600">
				Search and select scope — applied to{" "}
				<span className="font-semibold">{targetUserCount}</span> user
				{targetUserCount === 1 ? "" : "s"}.
			</p>

			<InputWithLabel
				label="Search project types, projects, RFx events"
				testId="scope-search-input"
				placeholder="Type to filter e.g. 'motorway', 'water', 'RFT-2026'…"
				value={query}
				onChange={(event) => handleQuery(event.target.value)}
			/>

			<div className="mt-2 rounded-lg border border-gray-200" data-testid="scope-results">
				{isLoading ? (
					<div className="px-3.5 py-2 text-sm text-gray-400" data-testid="scope-loading">
						Searching…
					</div>
				) : (
					<>
						{TYPE_ORDER.map((type) => {
							const options = groupedMatches[type];
							if (options.length === 0) {
								return null;
							}
							return (
								<div key={type}>
									<div className="bg-gray-50 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
										{TYPE_LABEL[type]}
									</div>
									{options.map((option) => {
										const key = scopeKey(option.scope_type, option.scope_id);
										const isSelected = selectedKeys.includes(key);
										return (
											<button
												type="button"
												key={key}
												onClick={() => onToggleScope(option)}
												data-testid={`scope-option-${key}`}
												aria-pressed={isSelected}
												/*
												 * A selected result was previously marked by bold text and a
												 * " · selected" suffix at the far right of the row — easy to
												 * miss while scanning a long list. It now carries the
												 * selection colour across the whole row, with a left bar so
												 * the state survives a quick vertical scan.
												 */
												className={cn(
													"flex w-full items-center gap-2.5 border-l-[3px] px-3.5 py-2 text-left text-sm",
													isSelected
														? "border-l-blue-500 bg-blue-50 hover:bg-blue-100"
														: "border-l-transparent hover:bg-gray-50"
												)}
											>
												<span
													className={cn(
														"flex-shrink-0",
														isSelected ? "text-blue-600" : "text-gray-500"
													)}
												>
													{TYPE_ICON[type]}
												</span>
												<span
													className={
														isSelected ? "font-bold text-blue-900" : "font-medium"
													}
												>
													{option.label}
												</span>
												<span className="ml-auto text-[11.5px] text-gray-500">
													{option.parent?.label ?? ""}
													{isSelected ? " · selected" : ""}
												</span>
											</button>
										);
									})}
								</div>
							);
						})}
						{matches.length === 0 && (
							<div className="px-3.5 py-2 text-sm text-gray-400" data-testid="scope-no-matches">
								No matches.
							</div>
						)}
					</>
				)}
			</div>

			<div className="mt-4" data-testid="scope-chip-container">
				<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
					Selected scope
				</p>
				{!hasSelection ? (
					<div className="flex min-h-[56px] items-center justify-center rounded-lg border border-dashed border-gray-200 text-xs text-gray-500">
						No scope selected yet — search above and click a result to add it.
					</div>
				) : (
					TYPE_ORDER.map((type) => {
						const options = selectedByType[type];
						if (options.length === 0) {
							return null;
						}
						return (
							<div key={type} className="mb-4">
								<h4 className="mb-2 text-[11.5px] font-semibold uppercase tracking-wide text-gray-500">
									{TYPE_LABEL[type]} ({options.length})
								</h4>
								<div className="flex flex-wrap gap-2 rounded-lg border border-dashed border-gray-200 p-3.5">
									{options.map((option) => {
										const key = scopeKey(option.scope_type, option.scope_id);
										return (
											<span
												key={key}
												// Portfolio green, Project yellow, RFx blue — the same
												// palette the access modal uses. Previously portfolios and
												// projects shared one blue, which made the two types
												// hardest to tell apart look identical.
												className={cn(
													"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs",
													scopeTypeStyle(type).chip
												)}
											>
												{option.label}
												<button
													type="button"
													onClick={() => onRemoveScope(option)}
													aria-label={`Remove ${option.label}`}
													data-testid={`scope-chip-remove-${key}`}
													className="opacity-65 hover:opacity-100"
												>
													<FiX size={12} />
												</button>
											</span>
										);
									})}
								</div>
							</div>
						);
					})
				)}
			</div>

			<ReadOnlyPermissionGrid roleName={roleName} entries={permissionGrid} />
		</div>
	);
};

interface ReadOnlyPermissionGridProps {
	roleName: string | null;
	entries: PermissionGridEntry[];
}

// AC4: the target role's permission grid, inline and read-only, so the admin sees WHAT is granted,
// not just where. Levels are server-authoritative (never client-computed).
const ReadOnlyPermissionGrid = ({ roleName, entries }: ReadOnlyPermissionGridProps) => {
	if (entries.length === 0) {
		return null;
	}
	return (
		<div className="mt-5" data-testid="permission-grid">
			<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
				{roleName ? `${roleName} — permissions (read-only)` : "Permissions (read-only)"}
			</p>
			<div className="overflow-x-auto rounded-lg border border-gray-200">
				<table className="w-full border-collapse text-sm">
					<thead>
						<tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
							<th className="px-3 py-2 font-semibold">Module</th>
							<th className="px-3 py-2 font-semibold">Level</th>
						</tr>
					</thead>
					<tbody>
						{entries.map((entry) => (
							<tr key={entry.module_id} className="border-t border-gray-100">
								<td className="px-3 py-1.5 text-gray-700">{entry.module_name}</td>
								<td className="px-3 py-1.5">
									<span
										className={cn(
											"rounded px-2 py-0.5 text-xs font-medium capitalize",
											entry.level === "none"
												? "bg-gray-100 text-gray-400"
												: "bg-blue-100 text-blue-700"
										)}
									>
										{entry.level}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};
